import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { AVAILABLE_L2_CHAIN_IDS, L2_BRIDGE_TOKEN_MAP } from '@/consts';
import { request } from '@/utils';

const select = <T extends Token>(tokens?: T[]) => {
  return tokens?.filter(
    x =>
      L2_BRIDGE_TOKEN_MAP[x.chain] &&
      Object.values(L2_BRIDGE_TOKEN_MAP[x.chain]).some(t => t.toLocaleLowerCase() === x.id),
  );
};

export const useSupportedWithdrawToken = (chainId?: string) => {
  const url = '/wallet/withdraw_token_list';

  return useQuery(
    [url, chainId],
    () =>
      request
        .get<
          Array<
            Token & {
              withdrawable_balance?: number;
            }
          >
        >(url, {
          chain_id: chainId!,
        })
        .then(x => {
          return x.sort((m, n) => (n.withdrawable_balance || 0) - (m.withdrawable_balance || 0));
        }),
    {
      select,
    },
  );
};

export const useSupportedDepositToken = (chainId?: string) => {
  return useQuery(
    ['/wallet/recharge_token_list', chainId],
    () =>
      request.get<Token[]>('/wallet/recharge_token_list', {
        chain_id: chainId!,
      }),
    {
      select,
    },
  );
};

const useTokens = (user_id?: string) => {
  const url = '/portfolio/all_token_list';

  return useQuery(
    [url, user_id],
    () => {
      return request
        .get<{ tokens: TokenWithAmount[] }>(url, {
          user_id: user_id!,
          chain_ids: AVAILABLE_L2_CHAIN_IDS.join(','),
        })
        .then(res => res.tokens);
    },
    {
      enabled: !!user_id,
    },
  );
};

export const useBridgeTokensList = (address?: string, tokens?: Token[]) => {
  const { data, isLoading: isAmountLoading } = useTokens(address);

  const tokenList = useMemo(() => {
    if (!data?.length || !tokens?.length) {
      return (tokens || []) as TokenWithAmount[];
    }

    const tokenDict = tokens.reduce((m: Record<string, TokenWithAmount>, n) => {
      m[`${n.chain}_${n.id}`] = n as TokenWithAmount;

      return m;
    }, {});

    const result: TokenWithAmount[] = [];

    data.forEach(x => {
      const id = `${x.chain}_${x.id}`;
      if (tokenDict[id]) {
        result.push({
          ...tokenDict[id],
          amount: x.amount,
          balance: x.balance,
        });

        delete tokenDict[id];
      }
    });

    result.sort((m, n) => (n.amount || 0) - (m.amount || 0));
    result.push(...Object.values(tokenDict));

    return result;
  }, [tokens, data]);

  return {
    tokenList,
    isAmountLoading,
  };
};
